import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  EscalationStatus,
  EscalationTableFields,
  IEscalationParams,
  PolicyEscalationType,
} from 'shared';
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';

import { useFetchEscalationsQuery } from '../services/api/escalationApi/escalation';
import { RootState } from '../store';
import {
  reset,
  resetFilter,
  resetPagination,
  resetSort,
  setFilterModel,
  setPaginationModel,
  setSortModel,
} from '../slices/escalationTableModels';

export const initialState: IEscalationParams = {
  organisationId: null,
  invoiceId: undefined,
  type: undefined,
  status: EscalationStatus.PENDING,
  scheduledDate: undefined,
  page: 1,
  limit: 100,
  searchText: undefined,
  includePaid: undefined,
  contactId: undefined,
  assignedUserId: undefined,
};

/** The params the escalations table query uses, built from the table models and the current route */
export function useEscalationParams(context?: `table`) {
  const escalationTableParams = useEscalationTableModels();
  const { organisationId, invoiceId, contactId, escalationId } = useParams();

  const {
    filterModel,
    sortModel,
    paginationModel,
  } = escalationTableParams;

  const page = paginationModel.page + 1;
  const limit = paginationModel.pageSize;

  const operatorMap = {
    [`onOrBefore`]: `<=`,
    [`onOrAfter`]: `>=`,
    [`is`]: `=`,
  };

  const operator = filterModel.items.find(i => i.field === `scheduledDate`)?.operator as `>=` | `<=` | `=` || undefined;
  const resolvedOperator = operator ? operatorMap[operator] : undefined;

  let paramObject: IEscalationParams = {
    page,
    limit,

    organisationId,
    invoiceId,
    type: filterModel.items.find(i => i.field === `type`)?.value as PolicyEscalationType || undefined,
    status: filterModel.items.find(i => i.field === `status`)?.value as EscalationStatus || undefined,
    scheduledDate: filterModel.items.find(i => i.field === `scheduledDate`)?.value as string || undefined,
    scheduledDateOperator: resolvedOperator,
    searchText: filterModel.quickFilterValues?.[0] || undefined,
    assignedUserId: filterModel.items.find(i => i.field === `assignee`)?.value as string || undefined,
    includePaid: filterModel.items.find(i => i.field === `paid`)?.value || undefined,
    groupId: filterModel.items.find(i => i.field === `groupId`)?.value ? Number(filterModel.items.find(i => i.field === `groupId`)?.value) : undefined,
    groupIdOperator: filterModel.items.find(i => i.field === `groupId`)?.operator as `is` | `not` | undefined,
    policyId: filterModel.items.find(i => i.field === EscalationTableFields.PolicyName)?.value ? Number(filterModel.items.find(i => i.field === EscalationTableFields.PolicyName)?.value) : undefined,
    contactTag: filterModel.items.find(i => i.field === EscalationTableFields.ContactTag)?.value as string || undefined,
    invoiceTag: filterModel.items.find(i => i.field === EscalationTableFields.InvoiceTag)?.value as string || undefined,
    frozen: filterModel.items.find(i => i.field === EscalationTableFields.Frozen)?.value as boolean | undefined,

    // Sort
    sortColumn: sortModel[0]?.field,
    sortDirection: sortModel[0]?.sort,
  };

  if (invoiceId) {
    paramObject = {
      ...initialState,
      invoiceId,
      organisationId,
    };
  }

  if (contactId) {
    paramObject.contactId = Number(contactId);
    paramObject.invoiceId = null;
  }

  if (escalationId && context !== `table`) {
    // When viewing a single escalation, then other escalations dont send the escalation ID
    paramObject = {
      ...initialState,
      includePaid: true,
      organisationId,
      escalationId: Number(escalationId),
    };
  }

  return paramObject;
}

export function useEscalations(context?: `table`) {
  const { invoiceId } = useParams();
  const paramObject = useEscalationParams(context);
  const { organisationId } = paramObject;

  const result = useFetchEscalationsQuery(paramObject, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
    pollingInterval: 1000 * 60, // 60 seconds
    skipPollingIfUnfocused: true,
  });

  if (result.data?.escalations && result.data.escalations.length && invoiceId) {
    result.data = {
      ...result.data,
      escalations: result.data.escalations.filter(e => e.invoiceId === invoiceId),
    };
  }

  // If no data yet, default to empty array
  if (!result.data) {
    result.data = {
      total: 0,
      escalations: [],
    };
  }

  if (!result.data?.escalations) {
    result.data.escalations = [];
  }

  return result;
}

export function useEscalationTableModels() {
  const dispatch = useDispatch();
  const escalationTableModels = useSelector((s: RootState) => s.escalationTableModels);

  function onReadyNow(currentTime?: Date) {
    dispatch(reset());

    dispatch(setFilterModel({
      items: [
        {
          field: EscalationTableFields.ScheduledDate,
          operator: `onOrBefore`,
          value: currentTime || new Date().toISOString(),
          id: Math.round(Math.random() * 1e5),
        },
        {
          field: EscalationTableFields.Status,
          operator: `is`,
          value: EscalationStatus.PENDING,
          id: Math.round(Math.random() * 1e5),
        },
      ],
    }));

    dispatch(setSortModel([
      {
        field: EscalationTableFields.ScheduledDate,
        sort: `asc`,
      },
    ]));
  }

  return {
    // State
    filterModel: escalationTableModels.filterModel,
    sortModel: escalationTableModels.sortModel,
    paginationModel: escalationTableModels.paginationModel,

    // Actions
    setFilterModel: (model: GridFilterModel) => dispatch(setFilterModel(model)),
    setSortModel: (model: GridSortModel) => dispatch(setSortModel(model)),
    setPaginationModel: (model: GridPaginationModel) => dispatch(setPaginationModel(model)),
    resetModels: () => dispatch(reset()),
    resetSort: () => dispatch(resetSort()),
    resetFilter: () => dispatch(resetFilter()),
    resetPagination: () => dispatch(resetPagination()),

    onReadyNow,
  };
}
